import { canAccessCrm } from "../../utils/crmAccess";
import { getCrmColors } from "../../theme/crmColors";
import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import {
  Chip,
  CircularProgress,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { v4 as uuidv4 } from "uuid";

import { AuthContext } from "../../context/Auth/AuthContext";
import toastError from "../../errors/toastError";
import {
  fetchKanbanContext,
  moveKanbanTicket,
  normalizeStageTarget
} from "../../services/kanban";

const useStyles = makeStyles(theme => ({
  formControl: {
    marginTop: theme.spacing(0.5),
    "& .MuiOutlinedInput-root": {
      borderRadius: 8,
      backgroundColor: theme.ticketDark ? getCrmColors(theme, "dark").inset : (theme.mode === "light" ? "#ffffff" : "#343434"),
      color: theme.ticketDark ? "#FFFFFF" : (theme.mode === "light" ? "#172033" : "#f8fafc")
    }
  },
  chip: {
    margin: 2,
    borderRadius: 6,
    fontWeight: 800
  },
  progress: {
    marginRight: theme.spacing(1)
  }
}));

const errorText = (err, moving = false) => {
  if (err?.response?.status === 401) return "Seu acesso expirou. Entre novamente para consultar a etapa Kanban.";
  if (err?.response?.status === 403) return "Você não tem acesso à etapa Kanban deste atendimento.";
  if (err?.response?.status === 404) return "A etapa Kanban deste atendimento não está disponível.";
  return moving
    ? "Não foi possível mover a etapa Kanban. Atualize a consulta para tentar novamente."
    : "Não foi possível carregar a etapa Kanban. Reabra os dados do contato para tentar novamente.";
};

export function TagsKanbanContainer({ ticket, enabled: visible = true }) {
  const classes = useStyles();
  const activeStatus = ["open", "pending"].includes(ticket?.status);
  // The backend permits Kanban only for active tickets. Apply the same guard
  // to reads, events, movements and late responses, not only to the selector.
  const { user, socket } = useContext(AuthContext);
  const crmAllowed = canAccessCrm(user);
  const enabled = visible && activeStatus && crmAllowed;
  const inactiveMessage = ticket?.status === "closed"
    ? "Reabra o atendimento para alterar a etapa Kanban."
    : "A etapa Kanban está disponível em atendimentos abertos ou aguardando.";
  const ticketId = ticket?.id;
  const companyId = user?.companyId;
  const [context, setContext] = useState({ tags: [], selected: "", version: 0 });
  const { tags, selected, version } = context;
  const [busy, setBusy] = useState(false);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState("");
  const busyRef = useRef(false);
  const refreshPendingRef = useRef(false);
  const refreshTimerRef = useRef(null);
  const mountedRef = useRef(true);
  const activeRef = useRef({ ticketId, enabled });
  activeRef.current = { ticketId, enabled };
  const requestRef = useRef(0);
  const movementRef = useRef(0);

  const isActive = useCallback(id => mountedRef.current && activeRef.current.enabled && activeRef.current.ticketId === id, []);

  const applyContext = useCallback(context => {
    setContext({ tags: context.stages || [], selected: context.stageTagId || "", version: Number(context.version) || 0 });
    setReady(true);
    setError("");
  }, []);

  const refreshTicket = useCallback(async (duringMovement = false) => {
    if (!enabled || !ticketId || !isActive(ticketId)) return;
    if (busyRef.current && !duringMovement) {
      refreshPendingRef.current = true;
      return;
    }
    refreshPendingRef.current = false;
    const request = ++requestRef.current;
    const isCurrent = () => isActive(ticketId) && request === requestRef.current;
    try {
      const { data } = await fetchKanbanContext(ticketId);
      if (isCurrent()) applyContext(data);
    } catch (err) {
      if (isCurrent()) {
        setReady(false);
        setError(errorText(err));
        if ([401, 403, 404].includes(err.response?.status)) setContext({ tags: [], selected: "", version: 0 });
        toastError(err);
      }
    }
  }, [applyContext, enabled, isActive, ticketId]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      requestRef.current += 1;
      movementRef.current += 1;
      clearTimeout(refreshTimerRef.current);
    };
  }, []);

  useEffect(() => {
    requestRef.current += 1;
    movementRef.current += 1;
    busyRef.current = false;
    refreshPendingRef.current = false;
    setContext({ tags: [], selected: "", version: 0 });
    setReady(false);
    setError("");
    setBusy(false);
    if (enabled && ticketId) refreshTicket();
  }, [enabled, refreshTicket, ticketId]);

  useEffect(() => {
    if (!enabled || !socket || !companyId || !ticketId) return undefined;
    const channel = `company-${companyId}-kanban`;
    const onInvalidate = data => {
      if (data?.action !== "invalidate") return;
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = setTimeout(() => refreshTicket(), 400);
    };
    socket.on(channel, onInvalidate);
    return () => {
      clearTimeout(refreshTimerRef.current);
      socket.off(channel, onInvalidate);
    };
  }, [companyId, enabled, refreshTicket, socket, ticketId]);

  const onChange = async event => {
    if (!enabled || !ticketId || !ready || error || busyRef.current) return;
    const previous = selected;
    let stageTagId;
    try {
      stageTagId = normalizeStageTarget(event.target.value);
    } catch (err) {
      toastError(err);
      return;
    }
    if ((previous || null) === stageTagId) return;

    busyRef.current = true;
    setBusy(true);
    requestRef.current += 1;
    const movementRequest = ++movementRef.current;
    const isCurrent = () => isActive(ticketId) && movementRequest === movementRef.current;
    try {
      const { data: movement } = await moveKanbanTicket({
        ticketId,
        stageTagId,
        expectedVersion: version,
        requestId: uuidv4()
      });
      if (isCurrent()) {
        setContext(current => ({ ...current, selected: movement.stageTagId || "", version: Number(movement.version) || version + 1 }));
        await refreshTicket(true);
      }
    } catch (err) {
      if (isCurrent()) {
        setContext(current => ({ ...current, selected: previous }));
        setReady(false);
        setError(errorText(err, true));
        if ([401, 403, 404].includes(err.response?.status)) setContext({ tags: [], selected: "", version: 0 });
        toastError(err);
        if (err.response?.status === 409) await refreshTicket(true);
      }
    } finally {
      if (isCurrent()) {
        busyRef.current = false;
        setBusy(false);
        if (refreshPendingRef.current) refreshTicket();
      }
    }
  };

  const renderSelectedValue = value => {
    if (!value) return "Sem etapa";
    const selectedTag = tags.find(tag => Number(tag.id) === Number(value));
    if (!selectedTag) return "Etapa indisponível";
    return (
      <Chip
        className={classes.chip}
        style={{ backgroundColor: selectedTag.color, color: "#fff" }}
        label={selectedTag.name}
        size="small"
      />
    );
  };

  if (!crmAllowed) return null;

  return (
    <FormControl fullWidth margin="dense" variant="outlined" className={classes.formControl} error={enabled && Boolean(error)}>
      <InputLabel id={`tag-kanban-${ticketId}`}>Etapa Kanban</InputLabel>
      <Select
        labelId={`tag-kanban-${ticketId}`}
        value={enabled ? selected : ""}
        label="Etapa Kanban"
        disabled={!enabled || !ticketId || busy || !ready || Boolean(error)}
        onChange={onChange}
        renderValue={renderSelectedValue}
        endAdornment={busy ? <CircularProgress className={classes.progress} size={18} /> : null}
        inputProps={{ "aria-label": `Mover atendimento ${ticketId} para` }}
        MenuProps={{
          anchorOrigin: { vertical: "bottom", horizontal: "left" },
          transformOrigin: { vertical: "top", horizontal: "left" },
          getContentAnchorEl: null
        }}
      >
        <MenuItem value="">Sem etapa</MenuItem>
        {tags.map(tag => <MenuItem key={tag.id} value={tag.id}>{tag.name}</MenuItem>)}
      </Select>
      {enabled && error && <FormHelperText role="alert">{error}</FormHelperText>}
      {visible && ticketId && !activeStatus && <FormHelperText>{inactiveMessage}</FormHelperText>}
    </FormControl>
  );
}
